import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Button,
  Modal,
  Typography,
  Autocomplete,
  TextField,
  Paper,
  InputAdornment,
} from "@mui/material";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import CloseIcon from "@mui/icons-material/Close";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";

const useStyles = makeStyles((theme) => ({
  modalContent: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: theme.palette.common.white,
    borderRadius: "16px",
    width: "min(500px, calc(100vw - 32px))",
    maxHeight: "min(450px, calc(100vh - 32px))",
    outline: "none",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 20px 0",
  },
  closeBtn: {
    cursor: "pointer",
    color: theme.palette.text?.secondary || "#666",
    "&:hover": { color: theme.palette.text?.primary || "#111" },
  },
  body: {
    padding: "20px",
    overflowY: "auto",
    flex: 1,
  },
  footer: {
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTop: `1px solid ${theme.palette.grey?.[200] || "#EEE"}`,
  },
  applyBtn: {
    borderRadius: "999px",
    padding: "10px 28px",
    fontWeight: 800,
    textTransform: "none",
    background: theme.palette.primary?.dark || theme.palette.text?.main || "#0F172A",
    color: theme.palette.common.white,
    "&:hover": {
      background: theme.palette.secondary.hover,
      color: theme.palette.text.main,
    },
    "&:disabled": {
      background: theme.palette.grey?.[300] || "#DDD",
      color: theme.palette.grey?.[500] || "#999",
    },
  },
  cancelBtn: {
    textTransform: "none",
    fontWeight: 700,
    color: theme.palette.text?.secondary || "#666",
    borderRadius: "999px",
    "&:hover": {
      borderRadius: "999px",
    },
  },
  autocompleteWrapper: {
    backgroundColor: theme.palette.grey?.[100] || "#F4F4F6",
    borderRadius: "20px",
    padding: "4px 16px",
  },
  autocomplete: {
    "& .MuiInputBase-root": {
      border: "none",
      padding: "0 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiAutocomplete-input": {
      padding: "8px 0 !important",
      color: theme.palette.text?.primary || "#111",
      "&::placeholder": {
        opacity: 0.7,
      },
    },
  },
  dropdownPaper: {
    borderRadius: "20px",
    marginTop: "8px",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    "& .MuiAutocomplete-listbox": {
      paddingTop: 0,
      cursor: "pointer",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },
  option: {
    padding: "12px 16px",
    "&:hover": {
      backgroundColor: theme.palette.action?.hover || "#F4F4F6",
    },
    "&[aria-selected='true']": {
      backgroundColor: theme.palette.common?.blue_grey || "#E8F4FD",
    },
  },
}));

// Sentinel option letting a recruiter add the candidate with no job offer attached,
// which creates a spontaneous application.
const NO_JOB_OFFER_ID = "__no_job_offer__";

const AddToKanbanModal = ({ open, onClose, candidate, onSuccess }) => {
  const classes = useStyles();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);

  const noJobOfferOption = useMemo(
    () => ({
      id: NO_JOB_OFFER_ID,
      job_title: intl.formatMessage({ id: "recruiter.cv_theque.no_job_offer_option" }),
    }),
    [intl]
  );

  const [jobOffers, setJobOffers] = useState([]);
  const [selectedJobOffer, setSelectedJobOffer] = useState(null);
  const [existingJobApplicationOfferIds, setExistingJobApplicationOfferIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && candidate) {
      fetchJobOffers();
      fetchExistingJobApplications();
    }
  }, [open, candidate]);

  // Nothing is preselected: going offer-less has to be picked on purpose.
  useEffect(() => {
    setSelectedJobOffer(null);
  }, [open]);

  const fetchJobOffers = () => {
    setLoading(true);
    sh.get("job_offer", {
      authenticity_token: authenticityToken,
      params: {
        offer_status: "online",
        page: 1,
      },
    })
      .then((res) => {
        setJobOffers(res.data?.data?.job_offers || []);
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(
          intl.formatMessage({ id: "recruiter.cv_theque.error_loading_job_offers" }),
          { variant: "error" }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchExistingJobApplications = () => {
    if (!candidate?.id) return;

    sh.get(`/candidate_job_application_offer_ids/${candidate.id}`)
      .then((res) => {
        setExistingJobApplicationOfferIds(res.data?.job_offer_ids || []);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleSubmit = () => {
    if (!candidate || !selectedJobOffer) return;

    const jobOfferId = selectedJobOffer.id !== NO_JOB_OFFER_ID ? selectedJobOffer.id : null;

    setSubmitting(true);
    sh.post("/add_candidate_to_kanban", {
      authenticity_token: authenticityToken,
      candidate_id: candidate.id,
      ...(jobOfferId ? { job_offer_id: jobOfferId } : {}),
    })
      .then((res) => {
        enqueueSnackbar(
          intl.formatMessage({ id: "recruiter.cv_theque.candidate_added_to_kanban" }),
          { variant: "success" }
        );
        if (onSuccess) {
          onSuccess(res.data);
        }
        onClose();
      })
      .catch((err) => {
        console.error(err);
        const errorMessage =
          err.response?.data?.error ||
          intl.formatMessage({ id: "recruiter.cv_theque.error_adding_to_kanban" });
        enqueueSnackbar(errorMessage, { variant: "error" });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const isJobOfferAlreadyLinked = (jobOffer) => {
    return existingJobApplicationOfferIds.includes(jobOffer.id);
  };

  const availableJobOffers = jobOffers.filter(
    (offer) => !isJobOfferAlreadyLinked(offer)
  );

  const jobOfferOptions = [noJobOfferOption, ...availableJobOffers];

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={classes.modalContent}>
        <Box className={classes.header}>
          <Typography variant="h6" fontWeight={700}>
            <FormattedMessage id="recruiter.cv_theque.add_to_kanban" />
          </Typography>
          <CloseIcon className={classes.closeBtn} onClick={onClose} />
        </Box>

        <Box className={classes.body}>
          {loading ? (
            <SquarehubLoader size={72} centered />
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" mb={2}>
                <FormattedMessage id="recruiter.cv_theque.select_job_offer_description" />
              </Typography>

              <Box className={classes.autocompleteWrapper}>
                <Autocomplete
                  className={classes.autocomplete}
                  options={jobOfferOptions}
                  getOptionLabel={(option) => option.job_title || ""}
                  value={selectedJobOffer}
                  onChange={(_, newValue) => setSelectedJobOffer(newValue)}
                  PaperComponent={({ children, ...props }) => (
                    <Paper {...props} className={classes.dropdownPaper}>
                      {children}
                    </Paper>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={intl.formatMessage({
                        id: "recruiter.cv_theque.select_job_offer",
                      })}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <WorkOutlineIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id} className={classes.option}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.job_title}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  noOptionsText={
                    <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: "center" }}>
                      {jobOffers.length > 0 ? (
                        <FormattedMessage id="recruiter.cv_theque.all_job_offers_linked" />
                      ) : (
                        <FormattedMessage id="recruiter.cv_theque.no_job_offers_available" />
                      )}
                    </Typography>
                  }
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </Box>

              {jobOffers.length > 0 &&
                availableJobOffers.length === 0 && (
                <Typography
                  variant="body2"
                  color="warning.main"
                  mt={2}
                  textAlign="center"
                >
                  <FormattedMessage id="recruiter.cv_theque.candidate_already_in_all_offers" />
                </Typography>
              )}
            </>
          )}
        </Box>

        <Box className={classes.footer}>
          <Button
            className={classes.cancelBtn}
            onClick={onClose}
            disabled={submitting}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            className={classes.applyBtn}
            onClick={handleSubmit}
            disabled={!selectedJobOffer || submitting || loading}
          >
            {submitting ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 24 }}>
                <SquarehubLoader size={22} />
              </Box>
            ) : (
              <FormattedMessage id="recruiter.cv_theque.add_to_kanban" />
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddToKanbanModal;
